import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, BookTemplate,
  Wand2, Settings, LogOut, Scale, UserCog, Store, Link2, Link2Off, Cog, History, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Panel", icon: LayoutDashboard, roles: ["admin", "abogado", "colaborador"] },
  { to: "/generador", label: "Generador IA", icon: Wand2, roles: ["admin", "abogado"] },
  { to: "/documentos-generados", label: "Documentos generados", icon: History, roles: ["admin", "abogado", "colaborador"] },
  { to: "/plantillas", label: "Plantillas", icon: BookTemplate, roles: ["admin", "abogado"] },
  { to: "/skills", label: "Skills IA", icon: Sparkles, roles: ["admin", "abogado"] },
  { to: "/marketplace", label: "Marketplace", icon: Store, roles: ["admin", "abogado"] },
  { to: "/documentos", label: "Biblioteca PDF/DOCX", icon: FileText, roles: ["admin", "abogado", "colaborador"] },
  { to: "/ajustes-ia", label: "Ajustes IA", icon: Settings, roles: ["admin", "abogado", "colaborador"] },
  { to: "/usuarios", label: "Usuarios", icon: UserCog, roles: ["admin"] },
  { to: "/ajustes-sistema", label: "Ajustes sistema", icon: Cog, roles: ["admin"] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [lexduo, setLexduo] = useState(null);

  useEffect(() => {
    api.get("/lexduo/status").then(({ data }) => setLexduo(data)).catch(() => setLexduo({ enabled: false }));
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const allowed = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <aside className="w-64 bg-white border-r border-[#E5E7EB] flex flex-col h-screen sticky top-0">
      <div className="px-6 py-6 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-[#7B1826] flex items-center justify-center rounded-sm">
            <Scale className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display text-lg leading-none text-[#0F172A]">Lexia</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
              Gestor Jurídico
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-4">
        {allowed.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${to.slice(1)}`}
            className={({ isActive }) =>
              "nav-link " + (isActive ? "nav-active font-medium" : "")
            }
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[#E5E7EB] px-4 py-2">
        <div
          className={`flex items-center gap-2 text-[10px] uppercase tracking-wider px-2 py-1.5 rounded-sm ${
            lexduo?.enabled
              ? "text-emerald-700 bg-emerald-50"
              : "text-neutral-500 bg-[#F3F4F6]"
          }`}
          title={lexduo?.enabled ? `Conectado a ${lexduo.base_url}` : "LexDuo no configurado en este entorno"}
          data-testid="lexduo-status-badge"
        >
          {lexduo?.enabled ? <Link2 className="w-3 h-3" /> : <Link2Off className="w-3 h-3" />}
          <span>LexDuo {lexduo?.enabled ? "conectado" : "offline"}</span>
        </div>
      </div>

      <div className="border-t border-[#E5E7EB] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm font-medium text-[#0F172A] truncate">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">{user?.role}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="p-2 text-neutral-500 hover:text-[#7B1826] hover:bg-[#F3F4F6] rounded-sm transition-colors"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
